import { useEffect } from "react";
import * as signalR from "@microsoft/signalr";
import { getSignalRConnection, recreateSignalRConnection } from "../services/Signalr";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AuthService from '../services/AuthService';
import { useWhatsAppMessage } from './WhatsAppMessageContext';

export default function SignalRContext({ children }) {
    const { handleNewMessage, incrementUnreadCount } = useWhatsAppMessage();

    useEffect(() => {
        let connection = getSignalRConnection();
        let isReconnecting = false;

        const startConnection = async () => {
            if (connection?.state === signalR.HubConnectionState.Disconnected && !isReconnecting) {
                try {
                    await connection?.start();
                    console.log("✅ SignalR connected");
                    isReconnecting = false;
                } catch (err) {
                    console.error("❌ SignalR connection error", err);
                    
                    // If 401 or unauthorized, try to refresh token and reconnect
                    if (err.statusCode === 401 || err.message?.includes('401') || err.message?.includes('Unauthorized')) {
                        console.log("🔄 Token expired, refreshing...");
                        try {
                            await AuthService.callRefreshToken();
                            // Recreate connection with new token
                            connection = recreateSignalRConnection();
                            isReconnecting = true;
                            await connection?.start();
                            console.log("✅ SignalR reconnected with new token");
                            isReconnecting = false;
                        } catch (refreshErr) {
                            console.error("❌ Failed to refresh token for SignalR", refreshErr);
                            isReconnecting = false;
                        }
                    }
                }
            }
        };

        startConnection();

        connection?.on("ReceiveMessage", (msg) => {
            console.log("📩 New message", msg);
            toast.info(msg.leadName + ": " + msg.message);
            const audio = new Audio("/notification.mp3");
            audio.play().catch(() => { });
            
            // Increment unread count if not on WhatsApp page
            const isOnWhatsAppPage = window.location.pathname.startsWith('/whatsapp');
            const isIncomingMessage = msg.direction !== 2; // 1 = incoming, 2 = outgoing
            
            if (!isOnWhatsAppPage && isIncomingMessage) {
                incrementUnreadCount(1);
            }
            
            // Notify WhatsAppChat about the new message
            handleNewMessage(msg);
        });

        connection?.onclose(async (error) => {
            if (error) {
                console.log("🔌 SignalR connection closed", error);
                
                // If closed due to 401, refresh token and reconnect
                if (error.statusCode === 401 || 
                    error.message?.includes('401') || 
                    error.message?.includes('Unauthorized') ||
                    error.message?.includes('authentication')) {
                    console.log("🔄 Connection closed due to 401, refreshing token...");
                    try {
                        await AuthService.callRefreshToken();
                        // Recreate connection with new token
                        connection = recreateSignalRConnection();
                        // Re-register message handler
                        connection?.on("ReceiveMessage", (msg) => {
                            console.log("📩 New message", msg);
                            toast.info(msg.leadName + ": " + msg.message);
                            const audio = new Audio("/notification.mp3");
                            audio.play().catch(() => { });
                            
                            // Increment unread count if not on WhatsApp page
                            const isOnWhatsAppPage = window.location.pathname.startsWith('/whatsapp');
                            const isIncomingMessage = msg.direction !== 2; // 1 = incoming, 2 = outgoing
                            
                            if (!isOnWhatsAppPage && isIncomingMessage) {
                                incrementUnreadCount(1);
                            }
                            
                            handleNewMessage(msg);
                        });
                        await connection?.start();
                        console.log("✅ SignalR reconnected after token refresh");
                        isReconnecting = false;
                    } catch (refreshErr) {
                        console.error("❌ Failed to refresh token", refreshErr);
                        isReconnecting = false;
                    }
                } else {
                    // For other errors, let automatic reconnect handle it
                    // The accessTokenFactory will get the latest token on reconnect
                }
            } else {
                // Normal close - automatic reconnect will handle it
                // The accessTokenFactory will get the latest token on reconnect
            }
        });

        return () => {
            connection?.off("ReceiveMessage");
        };
    }, [handleNewMessage, incrementUnreadCount]);

    return children;
}
